import React, { useState, useMemo, useEffect } from "react";
import { useTranslation } from "react-i18next";
import {
  Card,
  CardBody,
  CardHeader,
  CardTitle,
  Row,
  Col,
  Button,
  Dropdown,
  DropdownToggle,
  DropdownMenu,
  DropdownItem,
} from "reactstrap";
import { useNavigate, useSearchParams, useParams } from "react-router-dom";
import { getActiveColorStyle } from "utils/colorUtils";
import {
  downloadReportExportJob,
  enqueueReportExport,
  generateReport,
  generateReportPage,
  getReportExportJob,
} from "api/reports/api";
import AccountTransactionsReport from "../components/Reports/AccountTransactionsReport";

/** Excel via fetch+blob — waits for full file (no Chrome ~2KB "Resuming…" stall). */
const downloadExcelViaFetch = async ({ reportId, token, acctId, onStatus }) => {
  const apiBase = process.env.REACT_APP_API_URL || '';
  const url = `${apiBase}create-report/${reportId}?format=xls`;
  onStatus?.('Generating Excel…');

  const controller = new AbortController();
  const started = Date.now();
  const tick = setInterval(() => {
    const sec = Math.max(1, Math.round((Date.now() - started) / 1000));
    onStatus?.(`Generating Excel… ${sec}s`);
  }, 1000);

  try {
    const response = await fetch(url, {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        data: acctId ? { acct_id: acctId } : {},
      }),
      signal: controller.signal,
    });

    if (!response.ok) {
      const text = await response.text().catch(() => '');
      throw new Error(text || `Export failed (${response.status})`);
    }

    onStatus?.('Downloading file…');
    const blob = await response.blob();
    if (!blob || blob.size < 100) {
      throw new Error('Downloaded file is empty or incomplete.');
    }

    const disposition = response.headers.get('content-disposition') || '';
    const match = /filename\*?=(?:UTF-8'')?"?([^";]+)"?/i.exec(disposition);
    const fileName = match ? decodeURIComponent(match[1]) : `report-${reportId}.xls`;

    const objectUrl = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = objectUrl;
    a.download = fileName;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(objectUrl);
  } finally {
    clearInterval(tick);
  }
};

const ReportViewer = ({ activeColor = "blue", setShowViewer, reportInfo }) => {
  const [reportData, setReportData] = useState([]);
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { id } = useParams();

  // Export dropdown state
  const [exportDropdownOpen, setExportDropdownOpen] = useState(false);
  // Freeze once on mount so the header clock does not keep updating on re-render
  const [generatedAt] = useState(() => new Date().toLocaleString("en-US"));

  const recordAcctId = searchParams.get('acct_id');
  const shouldFetchReport = searchParams.get('view') === 'true' && !!id;

  // Start in loading when we will hit create-report (avoids flash of "No Data" while pending)
  const [isLoading, setIsLoading] = useState(shouldFetchReport);
  const [isDownloading, setIsDownloading] = useState(false);
  const [exportStatus, setExportStatus] = useState('');
  const [pagination, setPagination] = useState({
    page: 1,
    pageSize: 50,
    pageCount: 1,
    total: 0,
  });

  useEffect(() => {
    setPagination((current) => ({ ...current, page: 1 }));
  }, [id, recordAcctId]);

  useEffect(() => {
    const onMessage = (event) => {
      const msg = event?.data;
      if (!msg || msg.type !== 'dealertiq-report-page') return;
      const nextPage = Math.max(1, Number(msg.page) || 1);
      const nextPageSize = Math.min(200, Math.max(10, Number(msg.pageSize) || 50));
      setPagination((current) => {
        if (current.page === nextPage && current.pageSize === nextPageSize) {
          return current;
        }
        return {
          ...current,
          page: nextPage,
          pageSize: nextPageSize,
        };
      });
    };
    window.addEventListener('message', onMessage);
    return () => window.removeEventListener('message', onMessage);
  }, []);

  useEffect(() => {
    if (!shouldFetchReport) {
      setIsLoading(false);
      return;
    }

    let cancelled = false;

    const fetchReportData = async () => {
      setIsLoading(true);
      try {
        const runtimeParams = recordAcctId ? { acct_id: recordAcctId } : {};
        let data;
        try {
          data = await generateReportPage(
            id,
            pagination.page,
            pagination.pageSize,
            runtimeParams
          );
          if (data?.is_custom_handled && data?.report_type === 'account_notes_by_account') {
            data = await generateReport(id, null, runtimeParams);
          }
        } catch (pageError) {
          // Compatibility fallback while backend deployments roll between versions and for
          // special report renderers that do not support row pagination.
          console.warn('Paginated report preview unavailable, using legacy preview:', pageError);
          data = await generateReport(id, null, runtimeParams);
        }
        if (!cancelled) {
          const pageMeta = data?.meta?.pagination;
          if (typeof data?.html === 'string' && pageMeta) {
            setReportData(data.html);
            setPagination({
              page: Number(pageMeta.page) || 1,
              pageSize: Number(pageMeta.pageSize) || 50,
              pageCount: Number(pageMeta.pageCount) || 1,
              total: Number(pageMeta.total) || 0,
            });
          } else if (Array.isArray(data?.data) && pageMeta) {
            // Older preview payload without HTML — keep meta so a redeploy is obvious.
            setReportData(data.data);
            setPagination({
              page: Number(pageMeta.page) || 1,
              pageSize: Number(pageMeta.pageSize) || 50,
              pageCount: Number(pageMeta.pageCount) || 1,
              total: Number(pageMeta.total) || 0,
            });
          } else {
            setReportData(data);
          }
        }
      } catch (error) {
        console.error('ReportViewer - Error fetching report:', error);
        if (!cancelled) {
          setReportData([]);
        }
      } finally {
        if (!cancelled) {
          setIsLoading(false);
        }
      }
    };

    fetchReportData();

    return () => {
      cancelled = true;
    };
  }, [
    shouldFetchReport,
    id,
    recordAcctId,
    pagination.page,
    pagination.pageSize,
  ]);

  // Get active color style
  const activeColorStyle = useMemo(() => getActiveColorStyle(activeColor), [activeColor]);

  const handleBack = () => {
    const backParams = new URLSearchParams();
    if (recordAcctId) {
      backParams.set('acct_id', recordAcctId);
    }
    const query = backParams.toString();
    navigate(query ? `/reports/${id}?${query}` : `/reports/${id}`, { replace: true });
    setShowViewer(false);
  };


  const handleExport = (format) => {
    downloadReport(format);
  };


  const downloadReport = async (format) => {
    if (isDownloading) return;

    const token = localStorage.getItem('token');
    if (!token) {
      alert('Session information not found. Please login again.');
      return;
    }

    // Excel: fetch full file then save (avoids Chrome 2KB "Resuming…" on early stream headers).
    if (format === 'xlsx' || format === 'excel' || format === 'xls') {
      try {
        setIsDownloading(true);
        await downloadExcelViaFetch({
          reportId: id,
          token,
          acctId: recordAcctId,
          onStatus: setExportStatus,
        });
      } catch (error) {
        console.error('Export error:', error);
        alert(`Download failed: ${error.message}`);
      } finally {
        setIsDownloading(false);
        setExportStatus('');
      }
      return;
    }

    setIsDownloading(true);
    setExportStatus('Queuing…');
    const exportStartedAt = Date.now();
    try {
      const runtimeParams = recordAcctId ? { acct_id: recordAcctId } : {};
      let job = await enqueueReportExport(id, format, runtimeParams);
      if (!job?.id) throw new Error('Report could not be queued');

      const formatLabel = format === 'pdf' ? 'PDF' : 'Excel';
      const queuedDeadlineAt = Date.now() + 6 * 60 * 1000;
      const runningDeadlineAt = Date.now() + 90 * 60 * 1000;
      while (job.status === 'queued' || job.status === 'running') {
        const now = Date.now();
        if (job.status === 'queued' && now >= queuedDeadlineAt) {
          throw new Error(
            'Export is still queued — the report worker is not picking up jobs. Restart API/report-worker, then try again.',
          );
        }
        if (job.status === 'running' && now >= runningDeadlineAt) {
          throw new Error('Report is still processing. You can try downloading it later.');
        }
        const elapsedSec = Math.max(1, Math.round((now - exportStartedAt) / 1000));
        if (job.status === 'queued') {
          setExportStatus(`Queued… ${elapsedSec}s`);
        } else {
          setExportStatus(`Generating ${formatLabel}… ${elapsedSec}s`);
        }
        await new Promise((resolve) => setTimeout(resolve, 2000));
        job = await getReportExportJob(job.id);
      }
      if (job.status !== 'done') {
        throw new Error(job.error || `Report generation ${job.status}`);
      }

      setExportStatus('Downloading file…');
      const response = await downloadReportExportJob(job.id);
      const blob = response.data;
      if (blob.size === 0) {
        throw new Error('Downloaded file is empty. No data received from backend.');
      }

      const getFileExtension = (fmt) => {
        switch (fmt) {
          case 'pdf': return 'pdf';
          case 'xlsx': return 'xlsx';
          case 'excel': return 'xlsx';
          case 'csv': return 'csv';
          default: return fmt;
        }
      };

      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = job.file_name || `report-${job.id}.${getFileExtension(format)}`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Export error:', error);
      alert(`Download failed: ${error.message}`);
    } finally {
      setIsDownloading(false);
      setExportStatus('');
    }
  };

  // Custom report check
  const customData = reportData?.data?.data?.is_custom_handled ? reportData.data.data : reportData?.data?.is_custom_handled ? reportData.data : reportData?.is_custom_handled ? reportData : null;
  const isCustomHandled = !!customData?.is_custom_handled;
  const customReportType = customData?.report_type;
  const isAccountTransactionsReport =
    customReportType === 'account_transactions' ||
    customReportType === 'account_transactions_by_account_id' ||
    customReportType === 'account_transactions_by_account_prin_bal';
  const htmlContent =
    typeof reportData === 'string'
      ? reportData
      : reportData?.html || reportData?.data || reportData;
  const isHTMLReport = typeof htmlContent === 'string' && htmlContent.includes('<html>');

  return (
    <div className="content">
      {/* Header */}
      <Row>
        <Col md="12">
          <Card>
            <CardHeader>
              <div className="d-flex justify-content-between align-items-center">
                <div>
                  <CardTitle tag="h2" className="mb-0">
                    {t('reports.viewer.title')}
                  </CardTitle>
                  {reportInfo.name && (
                    <p className="text-muted mb-0 mt-1">
                      {reportInfo.name} - [{generatedAt}]
                      {process.env.NODE_ENV === 'development' ? (
                        <span className="ml-2 text-warning">
                          (dev HTML preview capped — Export = full data, may take a while)
                        </span>
                      ) : null}
                    </p>
                  )}
                </div>
                <div className="d-flex">
                  <Button
                    color="secondary"
                    onClick={handleBack}
                    className="mr-2 btn-round"
                  >
                    <i className="tim-icons icon-minimal-left" />
                    {t('common.back')}
                  </Button>

                  {/* Export Dropdown */}
                  <Dropdown isOpen={exportDropdownOpen} toggle={() => !isDownloading && setExportDropdownOpen(!exportDropdownOpen)}>
                    <DropdownToggle
                      caret
                      color="primary"
                      style={activeColorStyle}
                      className="btn-round"
                      disabled={isDownloading}
                    >
                      {isDownloading ? (
                        <>
                          <div className="spinner-border spinner-border-sm mr-2" role="status">
                            <span className="sr-only">Loading...</span>
                          </div>
                          {exportStatus || 'Generating…'}
                        </>
                      ) : (
                        <>
                          <i className="tim-icons icon-cloud-download-93" />
                          {t('reports.viewer.export')}
                        </>
                      )}
                    </DropdownToggle>
                    <DropdownMenu>
                      <DropdownItem
                        onClick={() => !isDownloading && handleExport('pdf')}
                        disabled={isDownloading}
                        style={{ opacity: isDownloading ? 0.5 : 1 }}
                      >
                        <i className="fas fa-file-pdf mr-2" style={{ color: '#dc3545' }} />
                        Download as PDF
                      </DropdownItem>
                      <DropdownItem
                        onClick={() => !isDownloading && handleExport('xlsx')}
                        disabled={isDownloading}
                        style={{ opacity: isDownloading ? 0.5 : 1 }}
                      >
                        <i className="fas fa-file-excel mr-2" style={{ color: '#28a745' }} />
                        Download as Excel
                      </DropdownItem>
                    </DropdownMenu>
                  </Dropdown>
                </div>
              </div>
            </CardHeader>
            <CardBody>
              {isLoading ? (
                <div className="text-center py-5" style={{ minHeight: '40vh' }}>
                  <div className="spinner-border text-primary" role="status" style={{ width: '3rem', height: '3rem' }}>
                    <span className="sr-only">{t('common.loading')}</span>
                  </div>
                  <h4 className="mt-3 text-muted">{t('reports.loadingReportData')}</h4>
                  <p className="text-muted mb-0">{t('reports.viewer.loadingDescription')}</p>
                </div>
              ) : isCustomHandled && isAccountTransactionsReport ? (
                <AccountTransactionsReport data={customData} reportInfo={reportInfo} />
              ) : isHTMLReport ? (
                <iframe
                  srcDoc={htmlContent}
                  style={{
                    width: '100%',
                    height: '80vh',
                    border: 'none',
                  }}
                  sandbox="allow-scripts allow-same-origin"
                  title="Report Content"
                />
              ) : (
                <div className="text-center py-5">
                  <i className="tim-icons icon-chart-bar-32" style={{ fontSize: '4rem', color: '#6c757d' }} />
                  <h4 className="mt-3 text-muted">{t('reports.viewer.noData')}</h4>
                  <p className="text-muted">{t('reports.viewer.noDataDescription')}</p>
                </div>
              )}
            </CardBody>
          </Card>
        </Col>
      </Row>
    </div>
  );
};

export default ReportViewer;
